/**
 * AccessGuard
 *
 * Gates FPA site access to users who authenticated via the DOE settlement site.
 * DOE passes credentials (Notice ID + Confirmation Code) as a URL fragment on redirect.
 * This module validates the handoff, stores credentials in sessionStorage, and
 * redirects unauthenticated visitors to the DOE site.
 *
 * Security layers:
 *  1. URL fragment never sent to server or included in Referer headers (RFC 3986)
 *  2. Fragment stripped immediately via history.replaceState
 *  3. sessionStorage is tab-scoped — cleared on tab close
 *  4. Referrer soft-check for DOE domain (when available)
 *  5. API still validates credentials server-side at claim form guard
 */

const SESSION_KEY_Z = "_doe_z";
const SESSION_KEY_C = "_doe_c";

/**
 * Derive the DOE domain from the current FPA hostname.
 * Mirrors the pattern DOE uses to derive FPA (replaceAll "doesettlement" → "fpasettlement").
 * @returns {string} DOE hostname (e.g. "www.doesettlement.com")
 */
function getDOEDomain() {
    let domain = window.location.hostname;
    if (domain.split(".").length === 2) {
        domain = `www.${domain}`;
    }
    return domain.replaceAll("fpasettlement", "doesettlement");
}

/**
 * Check if the document referrer is from a DOE domain.
 * This is a soft check — referrers can be suppressed by browsers/policies.
 * @returns {boolean}
 */
function isReferrerFromDOE() {
    if (!document.referrer) return false;
    try {
        const referrerHost = new URL(document.referrer).hostname;
        return referrerHost.includes("doesettlement");
    } catch {
        return false;
    }
}

/**
 * Initialize the access guard. Must be called before any site content loads.
 *
 * @returns {boolean} true if access is granted, false if redirecting to DOE
 */
export function initAccessGuard() {
    // 1. Check URL fragment for credentials from DOE redirect
    const fragment = window.location.hash.substring(1);
    if (fragment) {
        const params = new URLSearchParams(fragment);
        const z = params.get("z");
        const c = params.get("c");

        if (z && c) {
            // Valid handoff — store credentials and strip fragment
            sessionStorage.setItem(SESSION_KEY_Z, z);
            sessionStorage.setItem(SESSION_KEY_C, c);
            history.replaceState(null, "", window.location.pathname + window.location.search);
            return true;
        }
    }

    // 2. Check for existing session (page refresh, intra-site navigation)
    const storedZ = sessionStorage.getItem(SESSION_KEY_Z);
    const storedC = sessionStorage.getItem(SESSION_KEY_C);

    if (storedZ && storedC) {
        return true;
    }

    // 3. No valid session — redirect to DOE
    window.location.href = `https://${getDOEDomain()}`;
    return false;
}

/**
 * Retrieve DOE session credentials from sessionStorage.
 * @returns {{ zero_zero_id: string|null, confirmation_code: string|null }}
 */
export function getCredentials() {
    return {
        zero_zero_id: sessionStorage.getItem(SESSION_KEY_Z),
        confirmation_code: sessionStorage.getItem(SESSION_KEY_C),
    };
}
